import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { etsyAPI, walmartAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Loader2, X, FileSpreadsheet, Ban } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AddProductDialog } from '../components/listings/AddProductDialog';
import { DayGroup } from '../components/listings/DayGroup';
import { StageTabs, DateBar, SearchFilters, STAGE_TABS } from '../components/listings/ListingsToolbar';
import { dayKey, todayKey, shiftDayKey } from '../lib/workflow';

const DAYS_PAGE = 7;

const inDate = (key, preset, range) => {
  switch (preset) {
    case 'today': return key === todayKey();
    case 'yesterday': return key === shiftDayKey(-1);
    case 'last7': return key >= shiftDayKey(-6);
    case 'month': return key.slice(0, 7) === todayKey().slice(0, 7);
    case 'custom': {
      const from = range?.from ? dayKey(range.from.toISOString()) : null;
      const to = range?.to ? dayKey(range.to.toISOString()) : from;
      return (!from || key >= from) && (!to || key <= to);
    }
    default: return true;
  }
};

const matchesFilters = (p, search, f) => {
  if (search) {
    const s = search.toLowerCase();
    if (!(p.name || '').toLowerCase().includes(s) && !(p.best_title || '').toLowerCase().includes(s)) return false;
  }
  if (f.shape !== 'all' && p.shape !== f.shape) return false;
  if (f.video === 'with' && !p.has_video) return false;
  if (f.video === 'without' && p.has_video) return false;
  if (f.nfe === 'only' && !p.not_for_etsy) return false;
  if (f.nfe === 'hide' && p.not_for_etsy) return false;
  if (f.person !== 'all' && (p.workflow?.submitted_by_name || p.created_by_name) !== f.person) return false;
  return true;
};

export default function Etsy() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState(() => (STAGE_TABS.some((t) => t.id === searchParams.get('stage')) ? searchParams.get('stage') : 'all'));
  const [preset, setPreset] = useState('all');
  const [range, setRange] = useState(undefined);
  const [filters, setFilters] = useState({ shape: 'all', video: 'all', nfe: 'all', person: 'all' });
  const [view, setView] = useState(() => localStorage.getItem('listings-view') || 'grid');
  const [daysShown, setDaysShown] = useState(DAYS_PAGE);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [flagging, setFlagging] = useState(false);
  const [exportingSheet, setExportingSheet] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await etsyAPI.list();
      setProducts(res.data || []);
    } catch {
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem('listings-view', view); }, [view]);
  useEffect(() => { setDaysShown(DAYS_PAGE); }, [stage, preset, range, search, filters]);

  const shapes = useMemo(() => [...new Set(products.map((p) => p.shape).filter(Boolean))].sort(), [products]);
  const people = useMemo(() => [...new Set(products.map((p) => p.workflow?.submitted_by_name || p.created_by_name).filter(Boolean))].sort(), [products]);

  const base = useMemo(
    () => products.filter((p) => inDate(dayKey(p.created_at), preset, range) && matchesFilters(p, search, filters)),
    [products, preset, range, search, filters],
  );
  const counts = useMemo(() => base.reduce((m, p) => {
    const k = p.stage || 'none';
    return { ...m, [k]: (m[k] || 0) + 1 };
  }, { all: base.length }), [base]);
  const filtered = useMemo(
    () => base.filter((p) => stage === 'all' || (stage === 'none' ? !p.stage : p.stage === stage)),
    [base, stage],
  );
  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach((p) => {
      const k = dayKey(p.created_at);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    });
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectDay = (items, on) => setSelected((prev) => {
    const next = new Set(prev);
    items.forEach((p) => (on ? next.add(p.id) : next.delete(p.id)));
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const handleDelete = async (id) => {
    try {
      await etsyAPI.remove(id);
      toast.success('Deleted 🗑️');
      setDeleteId(null);
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const applyNotForEtsy = async (value) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      setFlagging(true);
      await etsyAPI.setNotForEtsy(ids, value);
      setProducts((prev) => prev.map((p) => (ids.includes(p.id) ? { ...p, not_for_etsy: value } : p)));
      toast.success(value ? `${ids.length} marked “Not for Etsy” 🚫` : `${ids.length} cleared`);
      clearSelection();
    } catch {
      toast.error('Failed to update listings');
    } finally {
      setFlagging(false);
    }
  };

  // Walmart's own MP_ITEM 5.0 workbook. No selection = the 5 newest photographed engagement rings.
  const exportWalmartSheet = async () => {
    const ids = Array.from(selected);
    try {
      setExportingSheet(true);
      const body = { image_base: process.env.REACT_APP_BACKEND_URL, limit: 5, ...(ids.length ? { product_ids: ids } : {}) };
      const res = await walmartAPI.exportSheet(body);
      const url = URL.createObjectURL(res.data);
      const match = /filename="?([^"]+)"?/.exec(res.headers['content-disposition'] || '');
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : 'walmart-engagement-rings.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const miss = Number(res.headers['x-walmart-skipped'] || 0);
      toast.success(`${res.headers['x-walmart-products']} listings · ${res.headers['x-walmart-rows']} size rows downloaded 📄` + (miss ? ` · ${miss} skipped (no photos yet)` : ''));
      const pre = await walmartAPI.exportSheetPreflight(body).catch(() => null);
      if (pre) {
        const prices = pre.data.products.map((pp) => pp.price).filter(Boolean);
        if (prices.length) {
          const lo = Math.min(...prices);
          const hi = Math.max(...prices);
          toast.message(lo === hi ? `Every row priced at $${lo.toLocaleString()}` : `Prices $${lo.toLocaleString()} – $${hi.toLocaleString()} (from the pricing matrix where the Walmart tab was blank)`);
        }
        pre.data.products.flatMap((pp) => pp.notes || []).slice(0, 3).forEach((n) => toast.warning(n, { duration: 10000 }));
      }
    } catch (err) {
      const detail = err.response?.data instanceof Blob
        ? await err.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return null; } })
        : err.response?.data?.detail;
      toast.error(detail || 'Could not build the Walmart sheet');
    } finally {
      setExportingSheet(false);
    }
  };

  const inPipeline = products.filter((p) => p.stage).length;

  return (
    <div className="space-y-5" data-testid="etsy-page">
      <div className="wf-card wf-rise p-6 bg-gradient-to-br from-[#FFF8EC] via-white to-[#FDF0EC] flex flex-col md:flex-row md:items-end gap-4">
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#C85A32]">Listings hub</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2623] mt-1">🛍️ Product listings</h1>
          <p className="text-sm text-[#6E635B] mt-1.5" data-testid="listings-summary">
            {products.length} listings · {inPipeline} in the team pipeline. Grouped by the day they were created.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportWalmartSheet} disabled={exportingSheet} className="h-10 rounded-2xl border-[#C9DEFA] text-[#1E568B] bg-white hover:bg-[#EBF3FA]" title="Download Walmart's MP_ITEM 5.0 workbook (5 newest photographed engagement rings, or your selection)" data-testid="walmart-sheet-export-btn">
            {exportingSheet ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Building…</> : <><FileSpreadsheet size={15} className="mr-1.5" /> Walmart sheet</>}
          </Button>
          <Button onClick={() => setShowAdd(true)} className="h-10 rounded-2xl bg-[#E07A5F] hover:bg-[#D0694E] text-white font-bold" data-testid="add-etsy-product-btn">
            <Plus size={15} className="mr-1.5" /> Add product
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <StageTabs value={stage} onChange={setStage} counts={counts} />
        <DateBar preset={preset} onPreset={setPreset} range={range} onRange={setRange} />
        <SearchFilters search={search} onSearch={setSearch} filters={filters} setFilters={setFilters} shapes={shapes} people={people} view={view} onView={setView} />
      </div>

      {selected.size > 0 && (
        <div className="sticky top-16 md:top-2 z-20 flex flex-wrap items-center gap-3 bg-[#2D2623] text-white rounded-2xl px-4 py-2.5 shadow-lg wf-pop" data-testid="etsy-selection-bar">
          <span className="text-sm font-bold" data-testid="etsy-selected-count">✔️ {selected.size} selected</span>
          <button onClick={() => setSelected(new Set(filtered.map((p) => p.id)))} className="text-xs text-white/70 hover:text-white underline underline-offset-2" data-testid="etsy-select-all">
            Select all shown ({filtered.length})
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <Button size="sm" variant="outline" disabled={exportingSheet} onClick={exportWalmartSheet} className="h-8 rounded-xl bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="etsy-walmart-sheet-selected">
              {exportingSheet ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <FileSpreadsheet size={13} className="mr-1.5" />} Walmart sheet
            </Button>
            <Button size="sm" disabled={flagging} onClick={() => applyNotForEtsy(true)} className="h-8 rounded-xl bg-amber-500 hover:bg-amber-600 text-white" data-testid="etsy-mark-not-for-etsy">
              {flagging ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Ban size={13} className="mr-1.5" />} Not for Etsy
            </Button>
            <Button size="sm" variant="outline" disabled={flagging} onClick={() => applyNotForEtsy(false)} className="h-8 rounded-xl bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="etsy-mark-for-etsy">Clear flag</Button>
            <button onClick={clearSelection} className="text-white/70 hover:text-white" data-testid="etsy-clear-selection" aria-label="Clear selection"><X size={16} /></button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 animate-pulse">
          {[...Array(8)].map((_, i) => <div key={i} className="h-64 rounded-2xl bg-[#F0EAE1]" />)}
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-[#E8E1D5] bg-white/60 p-16 text-center" data-testid="listings-empty">
          <div className="text-5xl mb-3 wf-float" aria-hidden>🔍</div>
          <p className="text-sm font-bold text-[#2D2623]">{products.length ? 'No listings match these filters' : 'No listings yet'}</p>
          <p className="text-xs text-[#9A8E85] mt-1">{products.length ? 'Try another date range or stage.' : 'Click “Add product” to create your first listing.'}</p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="etsy-product-grid">
          {groups.slice(0, daysShown).map(([k, items]) => (
            <DayGroup key={k} dayKey={k} items={items} view={view} selected={selected} onToggle={toggleSelect} onSelectDay={selectDay} onOpen={(id) => navigate(`/etsy/${id}`)} onDelete={setDeleteId} />
          ))}
          {groups.length > daysShown && (
            <div className="text-center">
              <Button variant="outline" onClick={() => setDaysShown((n) => n + DAYS_PAGE)} className="rounded-2xl border-[#E8E1D5] bg-white font-bold" data-testid="listings-show-older-btn">
                ⬇️ Show older days ({groups.length - daysShown} more)
              </Button>
            </div>
          )}
        </div>
      )}

      <AddProductDialog open={showAdd} onClose={() => setShowAdd(false)} onCreated={(p) => { setShowAdd(false); navigate(`/etsy/${p.id}`); }} />

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="sm:max-w-sm bg-white text-zinc-900 rounded-3xl">
          <DialogHeader><DialogTitle>Delete this listing? 🗑️</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6E635B]">The product pack and all generated content will be permanently removed.</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button className="flex-1 rounded-xl bg-red-600 text-white hover:bg-red-700" onClick={() => handleDelete(deleteId)} data-testid="confirm-delete-etsy-product">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
